var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { observable } from 'mobx';
import { CustomGradeCode } from '../constants/customGradeCodes';
import dictSet130 from '../constants/customGradeDicts/girdleBeauty/dictSet130';
import dictSet135 from '../constants/customGradeDicts/girdleBeauty/dictSet135';
import dictSet140 from '../constants/customGradeDicts/girdleBeauty/dictSet140';
import dictSet145 from '../constants/customGradeDicts/girdleBeauty/dictSet145';
import dictSet150 from '../constants/customGradeDicts/girdleBeauty/dictSet150';
import dictSet155 from '../constants/customGradeDicts/girdleBeauty/dictSet155';
import CustomGradingDictEntry from './CustomGradingDictEntry';
const LAB_COLOR_TO_REFERENCE_COLOR_MAP = {
    15: 1,
    16: 4,
    17: 7,
    18: 10,
    19: 13,
    20: 16,
    21: 19,
    22: 22,
    23: 25,
    1: 28,
    2: 31,
};
const getDistance = (p1, p2) => {
    return Number(Math.abs(p1 - p2).toFixed(3));
};
const findMinPointByDistance = (points, value) => {
    let result = points[0];
    if (!value) {
        return result;
    }
    let minDistance = getDistance(value, points[0]);
    for (let i = 1; i < points.length; i++) {
        const distance = getDistance(value, points[i]);
        if (distance <= minDistance) {
            minDistance = distance;
            result = points[i];
        }
    }
    return result;
};
const getColorInitialGradyByColor = (gradeId, dict) => {
    return dict.find(({ id }) => id === gradeId) || null;
};
const getDeadZoneInitialGradyByDeadZone = (grade, references, dict) => {
    if (grade === null) {
        return dict[Math.ceil(dict.length / 2)];
    }
    const numericGradeValue = +grade;
    let rightDeadZone = null;
    let leftDeadZone = null;
    for (let i = 0; i < references.length; i++) {
        const reference = references[i];
        const referenceDeadZone = reference.deadZoneMetric?.val;
        if (referenceDeadZone) {
            const numericReferenceDeadZone = +referenceDeadZone;
            if (!rightDeadZone) {
                rightDeadZone = numericReferenceDeadZone;
                continue;
            }
            if (numericGradeValue < numericReferenceDeadZone) {
                if (numericReferenceDeadZone < rightDeadZone) {
                    rightDeadZone = numericReferenceDeadZone;
                }
            }
            else {
                leftDeadZone = numericReferenceDeadZone;
            }
        }
    }
    const referenceOnLeftSide = references.find(({ deadZoneMetric }) => deadZoneMetric && +deadZoneMetric.val === leftDeadZone);
    const referenceOnRightSide = references.find(({ deadZoneMetric }) => deadZoneMetric && +deadZoneMetric.val === rightDeadZone);
    if (!referenceOnLeftSide) {
        return dict[0];
    }
    if (!referenceOnRightSide) {
        return dict[dict.length - 1];
    }
    const dictOnRightSideIndex = dict.findIndex(({ refId }) => refId === referenceOnRightSide.id);
    const dictOnLeftSideIndex = dict.findIndex(({ refId }) => refId === referenceOnLeftSide.id);
    if (dictOnLeftSideIndex === -1) {
        return dict[0];
    }
    if (dictOnRightSideIndex === -1) {
        return dict[dict.length - 1];
    }
    return dict[Math.floor((dictOnLeftSideIndex + dictOnRightSideIndex) / 2)];
};
const REFERENCE_RECEIVING_CUSTOM_STRATEGIES = {
    [CustomGradeCode.GirdleBeauty]: (product) => {
        const diameterRatio = product.reportData ? product.reportData.rt : null;
        const point = findMinPointByDistance([1.3, 1.35, 1.4, 1.45, 1.5, 1.55], diameterRatio);
        const dictMap = {
            1.3: dictSet130,
            1.35: dictSet135,
            1.4: dictSet140,
            1.45: dictSet145,
            1.5: dictSet150,
            1.55: dictSet155,
        };
        return dictMap[point];
    },
};
const SCALE_CUSTOM_NAMING_STRATEGIES = {
    [CustomGradeCode.GirdleBeauty]: (activeProduct) => {
        const diameterRatio = activeProduct.reportData ? activeProduct.reportData.rt : null;
        const point = findMinPointByDistance([1.3, 1.35, 1.4, 1.45, 1.5, 1.55], diameterRatio);
        const nameMap = {
            1.3: '1.3',
            1.35: '1.35',
            1.4: '1.4',
            1.45: '1.45',
            1.5: '1.5',
            1.55: '1.55',
        };
        return nameMap[point];
    },
};
const INITIAL_GRADE_RECEIVING_CUSTOM_STRATEGIES = {
    [CustomGradeCode.GirdleBeauty]: (_, __, dict) => {
        return dict[7];
    },
    [CustomGradeCode.Brilliance]: (_, __, dict) => {
        return dict[7];
    },
    [CustomGradeCode.Color]: (product, _, dict) => {
        let gradeId = product.customGrades?.[CustomGradeCode.Color]?.rawValue;
        if (!gradeId && product.color?.id) {
            gradeId = LAB_COLOR_TO_REFERENCE_COLOR_MAP[product.color.id];
        }
        return getColorInitialGradyByColor(gradeId ?? null, dict);
    },
    [CustomGradeCode.ColorViBox2]: (product, _, dict) => {
        let gradeId = product.customGrades?.[CustomGradeCode.ColorViBox2]?.rawValue;
        if (!gradeId && product.color?.id) {
            gradeId = LAB_COLOR_TO_REFERENCE_COLOR_MAP[product.color.id];
        }
        return getColorInitialGradyByColor(gradeId ?? null, dict);
    },
    [CustomGradeCode.DeadZone]: (product, references, dict) => {
        return getDeadZoneInitialGradyByDeadZone(product.customGrades?.[CustomGradeCode.DeadZone]?.rawValue ?? product.deadZoneMetric?.val ?? null, references, dict);
    },
};
export class CustomGradeAttributeModel {
    id;
    spId;
    title;
    dict;
}
export default class CustomGradeAttribute extends CustomGradeAttributeModel {
    dict;
    spId;
    title;
    constructor({ id, dict, spId, title }) {
        super();
        this.id = id;
        this.dict = dict ? dict.map((d) => new CustomGradingDictEntry(d)) : [];
        this.spId = spId;
        this.title = title;
    }
    getDictForProduct(product) {
        const customStrategy = REFERENCE_RECEIVING_CUSTOM_STRATEGIES[this.id];
        if (customStrategy) {
            return customStrategy(product);
        }
        return this.dict;
    }
    getReferencesIdsForProduct(product) {
        return this.getDictForProduct(product)
            .map((d) => d.refId)
            .filter((refId) => refId !== null);
    }
    getScaleTitle = (activeProduct) => {
        const customStrategy = SCALE_CUSTOM_NAMING_STRATEGIES[this.id];
        if (customStrategy) {
            return customStrategy(activeProduct);
        }
        return null;
    };
    getDefaultInitialGrade(product, references, dict) {
        return INITIAL_GRADE_RECEIVING_CUSTOM_STRATEGIES[this.id]?.(product, references, dict) ?? null;
    }
}
__decorate([
    observable
], CustomGradeAttribute.prototype, "dict", void 0);
__decorate([
    observable
], CustomGradeAttribute.prototype, "spId", void 0);
__decorate([
    observable
], CustomGradeAttribute.prototype, "title", void 0);
